import { validatorHelpers } from '@approvalmax/utils';
import { backend } from 'modules/data';
import { GetBeneficiarySchema } from 'shared/data/webApp/v1';
import { z } from 'zod';

import { stepOneRenderedKeys, stepTwoRenderedKeys } from './AddBeneficiaryPopupContent.constants';
import { getFieldByPath } from './AddBeneficiaryPopupContent.helpers';
import { DeepValidationField, ValidatedStringType } from './AddBeneficiaryPopupContent.types';

export const getFieldValidation = (
    value: string,
    field: GetBeneficiarySchema['response']['schema']['fields'][number] | undefined,
    activeStep: number
) => {
    if (!field?.path || !field.enabled) return true;

    let shouldValidate = false;

    switch (activeStep) {
        case 1:
            shouldValidate = stepOneRenderedKeys.includes(field.path);
            break;
        case 2:
            shouldValidate = stepTwoRenderedKeys.includes(field.path);
            break;
        case 3:
            shouldValidate = true;
            break;
    }

    if (!shouldValidate) return true;

    if (field.required && !validatorHelpers.requiredString()(value)) {
        return false;
    }

    const pattern = field.rule?.pattern ? new RegExp(field.rule.pattern) : undefined;

    return pattern && value && value?.length > 0 ? pattern.test(value) : true;
};

export const getFieldsValidationSchema = (
    fieldsSchema: backend.AirwallexBenefeciarySchemaField[],
    activeStep: number
) => {
    const createValidatedString = (path: string): ValidatedStringType =>
        z
            .string({ coerce: true })
            .refine((value) => getFieldValidation(value, getFieldByPath(path, fieldsSchema), activeStep), {
                message: '',
            })
            .optional();

    const createNestedSchema = (paths: string[]) => {
        const schema: Record<string, ValidatedStringType | DeepValidationField> = {};

        /**
         * Iterates through each path and constructs a nested validation schema structure.
         * For example, for path 'beneficiary.bank_details.account_number':
         * 1. Splits path into parts: ['beneficiary', 'bank_details', 'account_number']
         * 2. Creates nested objects: { beneficiary: { bank_details: { account_number: validationRule } } }
         * 3. For the last part (leaf node), assigns validation rule using createValidatedString
         * 4. Ensures proper nesting and prevents overwriting existing nested objects
         */
        paths.forEach((path) => {
            const parts = path.split('.');

            let current = schema;

            parts.forEach((part, index) => {
                if (index === parts.length - 1) {
                    current[part] = createValidatedString(path);
                } else {
                    if (!current[part]) {
                        current[part] = {};
                    }

                    if (!(current[part] instanceof z.ZodType)) {
                        current = current[part];
                    }
                }
            });
        });

        /**
         * Recursively converts the nested object structure into a Zod validation schema.
         * For each property in the object:
         * - If the value is already a Zod type (leaf node), adds it directly to the schema
         * - If the value is a nested object, recursively converts it to a Zod schema
         *
         * Example input:
         * {
         *   beneficiary: {
         *     bank_details: {
         *       account_number: z.string()
         *     }
         *   }
         * }
         *
         * Example output:
         * z.object({
         *   beneficiary: z.object({
         *     bank_details: z.object({
         *       account_number: z.string()
         *     })
         *   })
         * })
         */
        const convertToZodSchema = (obj: Record<string, ValidatedStringType | DeepValidationField>) => {
            const entries = Object.entries(obj);

            const zodObj: z.ZodRawShape = {};

            entries.forEach(([key, value]) => {
                if (value instanceof z.ZodType) {
                    zodObj[key] = value;
                } else if (typeof value === 'object' && value !== null) {
                    zodObj[key] = convertToZodSchema(value);
                }
            });

            return z.object(zodObj);
        };

        return convertToZodSchema(schema);
    };

    return createNestedSchema(fieldsSchema.map((field) => field.path));
};
